'use no memo';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;

type CompactRelativeTime = {
	count: number;
	unit: Intl.RelativeTimeFormatUnit;
	updateAfter: number;
};

const getCompactRelativeTime = (timestamp: number): CompactRelativeTime => {
	const elapsed = Math.max(0, Date.now() - timestamp);

	if (elapsed < HOUR_IN_MS) {
		const count = Math.max(1, Math.floor(elapsed / MINUTE_IN_MS));
		return {
			count,
			unit: 'minute',
			updateAfter: Math.min((count + 1) * MINUTE_IN_MS, HOUR_IN_MS) - elapsed,
		};
	}

	if (elapsed < DAY_IN_MS) {
		const count = Math.floor(elapsed / HOUR_IN_MS);
		return {
			count,
			unit: 'hour',
			updateAfter: Math.min((count + 1) * HOUR_IN_MS, DAY_IN_MS) - elapsed,
		};
	}

	const count = Math.floor(elapsed / DAY_IN_MS);
	return {
		count,
		unit: 'day',
		updateAfter: (count + 1) * DAY_IN_MS - elapsed,
	};
};

export const LastActive = ({ timestamp }: { timestamp: number }): React.JSX.Element => {
	const intl = useIntl();
	const [, setRenders] = useState(0);
	const { count, unit, updateAfter } = getCompactRelativeTime(timestamp);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setRenders((renders) => renders + 1);
		}, updateAfter);

		return () => clearTimeout(timeout);
	}, [updateAfter]);

	return <>{intl.formatRelativeTime(-count, unit, { numeric: 'always', style: 'narrow' })}</>;
};
